import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { adminApi } from '../lib/api'
import { transformArray } from '../lib/transformers'
import { Search, MapPin, Loader2 } from 'lucide-react'
import { GoogleMap, InfoWindowF, MarkerF } from '@react-google-maps/api'
import { useGoogleMaps } from '../components/maps/GoogleMapsProvider'
import { toNumericCoordinate } from '../utils/location'

interface SearchLocationResult {
  id: string
  type?: string
  title: string
  description?: string
  location: {
    lat: number
    lng: number
    address?: string
  }
}

const defaultMapCenter = { lat: 40.7128, lng: -74.006 }

const buildLocationKey = (result: any, index: number) =>
  String(result?.id ?? `${result?.type || 'item'}-${index}`)

const searchMapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
  ],
}

export default function SearchPage() {
  const [query, setQuery] = useState('')
  const [type, setType] = useState('all')
  const [results, setResults] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const { isLoaded, loadError } = useGoogleMaps()
  const mapRef = useRef<google.maps.Map | null>(null)
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null)
  const [mapCenter, setMapCenter] = useState(defaultMapCenter)
  const [mapZoom, setMapZoom] = useState(3)

  const locationResults = useMemo<SearchLocationResult[]>(() => {
    const items: SearchLocationResult[] = []
    results.forEach((result, index) => {
      const candidates = [
        result.location,
        result.trip?.location,
        result.event?.location,
        result.trip?.trip?.location,
      ]
      for (const candidate of candidates) {
        if (!candidate) continue
        const lat = toNumericCoordinate(candidate.lat)
        const lng = toNumericCoordinate(candidate.lng)
        if (lat === null || lng === null) continue
        const title =
          result.title ||
          result.displayName ||
          result.username ||
          (typeof result.content === 'string' ? result.content.slice(0, 50) : '') ||
          'Untitled'
        items.push({
          id: buildLocationKey(result, index),
          type: result.type,
          title,
          description: result.description || result.bio || result.content,
          location: {
            lat,
            lng,
            address: candidate.address || candidate.name || candidate.description,
          },
        })
        break
      }
    })
    return items
  }, [results])

  const handleMapIdle = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    const center = map.getCenter()
    if (center) {
      setMapCenter({ lat: center.lat(), lng: center.lng() })
    }
    setMapZoom(map.getZoom() || 3)
  }, [])

  const selectedLocation = useMemo(
    () => locationResults.find((item) => item.id === selectedLocationId) || null,
    [locationResults, selectedLocationId],
  )

  const locationLookup = useMemo(() => {
    const map = new Map<string, SearchLocationResult>()
    for (const item of locationResults) {
      map.set(item.id, item)
    }
    return map
  }, [locationResults])

  const handleFocusOnResult = useCallback((item: SearchLocationResult) => {
    setSelectedLocationId(item.id)
    setMapCenter(item.location)
    setMapZoom(10)
    const map = mapRef.current
    if (map) {
      map.panTo(item.location)
      map.setZoom(10)
    }
  }, [])

  useEffect(() => {
    if (locationResults.length === 0) {
      setSelectedLocationId(null)
      setMapCenter(defaultMapCenter)
      setMapZoom(3)
      return
    }
    const isSelectedStillVisible = locationResults.some(item => item.id === selectedLocationId)
    if (!isSelectedStillVisible) {
      const first = locationResults[0]
      setSelectedLocationId(first.id)
      setMapCenter(first.location)
      setMapZoom(6)
    }
  }, [locationResults, selectedLocationId])

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim()) return

    setLoading(true)
    try {
      const response = await adminApi.search(query, type !== 'all' ? type : undefined)
      let rawData = []
      if (!response.data) {
        rawData = []
      } else if (Array.isArray(response.data)) {
        rawData = response.data
      } else if (typeof response.data === 'object') {
        // API returns {success: true, data: {results: [], pagination: {}}}
        rawData = response.data?.data?.results || response.data?.results || response.data?.data || response.data?.items || []
      }
      // Transform snake_case to camelCase
      const data = transformArray<any>(Array.isArray(rawData) ? rawData : [])
      setResults(data)
    } catch (error) {
      console.error('Search failed:', error)
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Search</h1>
        <p className="text-gray-600 mt-1">Search across all resources</p>
      </div>

      <div className="card">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for users, trips, events, photos..."
                className="input w-full"
                autoFocus
              />
            </div>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="input w-40"
            >
              <option value="all">All</option>
              <option value="users">Users</option>
              <option value="trips">Trips</option>
              <option value="events">Events</option>
              <option value="photos">Photos</option>
              <option value="comments">Comments</option>
            </select>
            <button type="submit" className="btn-primary flex items-center gap-2">
              <Search className="h-4 w-4" />
              Search
            </button>
          </div>
        </form>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Searching...</div>
        ) : results.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {query ? 'No results found' : 'Enter a search query to get started'}
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {results.map((result, index) => {
              const locationKey = buildLocationKey(result, index)
              const locationItem = locationLookup.get(locationKey)
              return (
                <div key={locationKey} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="badge badge-info">{result.type}</span>
                        <span className="font-medium text-gray-900">
                          {result.title || result.displayName || result.username || result.content?.slice(0, 50)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {result.description || result.bio || result.email || result.content}
                      </p>
                      {locationItem && (
                        <div className="mt-2 flex flex-col gap-1">
                          {locationItem.location.address && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {locationItem.location.address}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => handleFocusOnResult(locationItem)}
                            className="text-xs text-blue-600 hover:underline self-start"
                          >
                            Show on map
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {locationResults.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Map View</h2>
              <p className="text-sm text-gray-600">Locations for search results that include coordinates</p>
            </div>
            <span className="text-xs uppercase tracking-wide text-gray-500">
              {locationResults.length} with locations
            </span>
          </div>

          {loadError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 mb-4">
              Failed to load Google Maps. Please check your API key configuration.
            </div>
          )}

          <div className="relative h-80 border border-gray-200 rounded-lg overflow-hidden">
            {isLoaded ? (
              <GoogleMap
                center={selectedLocation?.location || mapCenter}
                zoom={mapZoom}
                options={searchMapOptions}
                onLoad={(map) => {
                  mapRef.current = map
                  handleMapIdle()
                }}
                onIdle={handleMapIdle}
                mapContainerStyle={{ width: '100%', height: '100%' }}
              >
                {locationResults.map((item) => (
                  <MarkerF
                    key={item.id}
                    position={item.location}
                    onClick={() => handleFocusOnResult(item)}
                    label={{
                      text: (item.type || 'item').slice(0, 1).toUpperCase(),
                      color: '#ffffff',
                      fontWeight: 'bold',
                    }}
                    icon={{
                      path: google.maps.SymbolPath.CIRCLE,
                      scale: selectedLocation?.id === item.id ? 10 : 8,
                      fillColor: item.type === 'trip' ? '#3B82F6' : item.type === 'event' ? '#F97316' : '#10B981',
                      fillOpacity: 1,
                      strokeColor: '#ffffff',
                      strokeWeight: 2,
                    }}
                  />
                ))}

                {selectedLocation && (
                  <InfoWindowF
                    position={selectedLocation.location}
                    onCloseClick={() => setSelectedLocationId(null)}
                  >
                    <div className="max-w-xs space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="badge badge-info">{selectedLocation.type || 'item'}</span>
                        <span className="font-semibold text-gray-900 text-sm">{selectedLocation.title}</span>
                      </div>
                      {selectedLocation.location.address && (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {selectedLocation.location.address}
                        </p>
                      )}
                      {selectedLocation.description && (
                        <p className="text-xs text-gray-500 line-clamp-3">{selectedLocation.description}</p>
                      )}
                    </div>
                  </InfoWindowF>
                )}
              </GoogleMap>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm bg-gray-100">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading map…
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
